/**
 * @description
 * API key management modal component for managing user API keys.
 * Uses Radix UI Dialog pattern with proper form validation and security features.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Key, Eye, EyeOff, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { ClientUser, ApiKey } from '../../services/api';

interface ApiKeyManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  user: ClientUser;
  onKeyAdded: (key: ApiKey) => void;
  onKeyDeleted: (keyId: string) => void;
}

interface ApiKeyFormData {
  provider: 'google_ai' | 'openai' | 'anthropic' | 'groq' | 'openrouter' | 'cerebras';
  keyName: string;
  keyValue: string;
  isActive: boolean;
}

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI', placeholder: 'sk-proj-...' },
  { value: 'anthropic', label: 'Anthropic', placeholder: 'sk-ant-...' },
  { value: 'google_ai', label: 'Google AI', placeholder: 'AIzaSy...' },
  { value: 'groq', label: 'Groq', placeholder: 'gsk_...' },
  { value: 'openrouter', label: 'OpenRouter', placeholder: 'sk-or-...' },
  { value: 'cerebras', label: 'Cerebras', placeholder: 'csk-...' },
];

/**
 * API key management modal component
 */
export function ApiKeyManagementModal({ 
  isOpen, 
  onClose, 
  clientId, 
  user, 
  onKeyAdded, 
  onKeyDeleted 
}: ApiKeyManagementModalProps) {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showKeyValue, setShowKeyValue] = useState(false);
  const [formData, setFormData] = useState<ApiKeyFormData>({
    provider: 'openai',
    keyName: '',
    keyValue: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getUserApiKeys(clientId, user.id);
      setApiKeys(response.apiKeys);
    } catch (error) {
      console.error('Error loading API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [clientId, user.id]);

  // Load API keys when modal opens
  useEffect(() => {
    if (isOpen) {
      loadApiKeys();
      resetForm();
    }
  }, [isOpen, loadApiKeys]);

  const resetForm = () => {
    setFormData({
      provider: 'openai',
      keyName: '',
      keyValue: '',
      isActive: true,
    });
    setErrors({});
    setShowKeyValue(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.provider) {
      newErrors.provider = 'Provider is required';
    }

    if (!formData.keyName.trim()) {
      newErrors.keyName = 'Key name is required';
    }

    if (!formData.keyValue.trim()) {
      newErrors.keyValue = 'API key is required';
    } else {
      // Basic format validation
      const provider = PROVIDER_OPTIONS.find(p => p.value === formData.provider);
      if (provider && !formData.keyValue.startsWith(provider.placeholder.split('...')[0])) {
        newErrors.keyValue = `Invalid ${provider.label} API key format`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddKey = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    
    try {
      const response = await api.addUserApiKey(clientId, user.id, {
        provider: formData.provider,
        keyName: formData.keyName,
        keyValue: formData.keyValue,
        isActive: formData.isActive,
      });
      
      onKeyAdded(response.apiKey);
      resetForm();
      toast.success('API key added successfully');
    } catch (error) {
      console.error('Error adding API key:', error);
      toast.error(`Failed to add API key: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await api.deleteUserApiKey(clientId, user.id, keyId);
      onKeyDeleted(keyId);
      toast.success('API key deleted successfully');
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const handleInputChange = (field: keyof ApiKeyFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getProviderLabel = (provider: string) => {
    return PROVIDER_OPTIONS.find(p => p.value === provider)?.label || provider;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-4xl rounded-lg border bg-card shadow-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-card-foreground">
              Manage API Keys for {user.userName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col h-full max-h-[calc(90vh-80px)]">
          {/* Existing API Keys */}
          <div className="flex-1 p-6 overflow-y-auto">
            <h3 className="text-md font-medium text-card-foreground mb-4">
              Existing API Keys
            </h3>
            
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No API keys found for this user.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg bg-background"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          {getProviderLabel(key.provider)}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          ({key.keyName})
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          key.isActive 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {key.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground font-mono">
                        {key.keyPreview}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteKey(key.id)}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add New API Key Form */}
          <div className="border-t border-border p-6">
            <h3 className="text-md font-medium text-card-foreground mb-4">
              Add New API Key
            </h3>
            
            <form onSubmit={handleAddKey} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Provider */}
                <div>
                  <label htmlFor="provider" className="block text-sm font-medium text-card-foreground mb-1">
                    Provider *
                  </label>
                  <select
                    id="provider"
                    value={formData.provider}
                    onChange={(e) => handleInputChange('provider', e.target.value as 'google_ai' | 'openai' | 'anthropic' | 'groq' | 'openrouter' | 'cerebras')}
                    disabled={saving}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground ${
                      errors.provider ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    {PROVIDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.provider && (
                    <p className="mt-1 text-sm text-destructive">{errors.provider}</p>
                  )}
                </div>

                {/* Key Name */}
                <div>
                  <label htmlFor="keyName" className="block text-sm font-medium text-card-foreground mb-1">
                    Key Name *
                  </label>
                  <input
                    type="text"
                    id="keyName"
                    value={formData.keyName}
                    onChange={(e) => handleInputChange('keyName', e.target.value)}
                    disabled={saving}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground ${
                      errors.keyName ? 'border-destructive' : 'border-border'
                    }`}
                    placeholder="e.g., Production Key, Development Key"
                  />
                  {errors.keyName && (
                    <p className="mt-1 text-sm text-destructive">{errors.keyName}</p>
                  )}
                </div>
              </div>

              {/* API Key Value */}
              <div>
                <label htmlFor="keyValue" className="block text-sm font-medium text-card-foreground mb-1">
                  API Key *
                </label>
                <div className="relative">
                  <input
                    type={showKeyValue ? 'text' : 'password'}
                    id="keyValue"
                    value={formData.keyValue}
                    onChange={(e) => handleInputChange('keyValue', e.target.value)}
                    disabled={saving}
                    className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground font-mono ${
                      errors.keyValue ? 'border-destructive' : 'border-border'
                    }`}
                    placeholder={PROVIDER_OPTIONS.find(p => p.value === formData.provider)?.placeholder}
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeyValue(!showKeyValue)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  >
                    {showKeyValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.keyValue && (
                  <p className="mt-1 text-sm text-destructive">{errors.keyValue}</p>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  disabled={saving}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-ring focus:ring-2"
                />
                <label htmlFor="isActive" className="text-sm text-card-foreground">
                  Active (key will be available for use)
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span>Add API Key</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
